import {Localized, useLocalization} from "@fluent/react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
	Collapse,
	Container,
	IconButton,
	Skeleton,
	Stack,
	Table,
	TableBody,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import React, {useEffect, useState} from "react";

import Widget from "../analytics/widget/Widget";
import WidgetBody from "../analytics/widget/WidgetBody";
import WidgetTitle from "../analytics/widget/WidgetTitle";
import {addToDate} from "../../helpers/dateTimeHelpers";
import parseDate from "../../helpers/parseDate";
import useDateFormat from "../../i18n/useDateFormat";
import useDateTimeFormat from "../../i18n/useDateTimeFormat";
import type FetchStatus from "../../store/FetchStatus";
import type {
	CourseNotificationSearchResult,
	SentNotificationStatistics,
} from "../../store/services/notificationsService";
import notificationsService from "../../store/services/notificationsService";
import StudentName from "../students/StudentName";
import type User from "../../store/users/User";
import randomN from "../../utils/randomN";
import TableCell from "../../utils/tables/Cell";
import createActionColumnDef from "../../utils/tables/createActionColumnDef";
import type {ColumnDefs} from "../../utils/tables/Head";
import LoadingErrorState from "../../utils/tables/LoadingErrorState";
import ServerPaginationControls from "../../utils/tables/ServerPaginationControls";
import usePaginationState from "../../utils/tables/usePaginationState";

function SentNotificationsWidget(props: {courseId: number}) {
	return (
		<Widget id="course-settings-notifications-list">
			<WidgetTitle>
				<Localized id="sent-notifications-widget-title">
					Notifications sent in the last 30 days
				</Localized>
			</WidgetTitle>
			<WidgetBody>
				<NotificationsTable courseId={props.courseId} />
			</WidgetBody>
		</Widget>
	);
}

const columnKeys = ["notification", "recipients", "date"] as const;

const columnDefs: ColumnDefs<
	typeof columnKeys[number],
	keyof SentNotificationStatistics
> = {
	notification: {
		name: (
			<Localized id="sent-notifications-widget-column-notification">
				Notification
			</Localized>
		),
		width: "50%",
	},
	recipients: {
		name: (
			<Localized id="sent-notifications-widget-column-recipients">
				Recipients total
			</Localized>
		),
		width: "20%",
	},
	date: {
		name: (
			<Localized id="sent-notifications-widget-column-sent-date">
				Sent date
			</Localized>
		),
	},
};

const actionColumn = createActionColumnDef();

const columnNumber = columnKeys.length + 1;

const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const daysBeforeToday = -30;

function NotificationsTable(props: {courseId: number}) {
	const [pageSize, setPageSize] = useState(10);

	const {
		page: notificationsPage,
		fetchFirstPage,
		fetchRelatedPage,
		pageFetchStatus,
		retryFetching,
	} = usePaginationState<SentNotificationStatistics, string>();

	const notifications = notificationsPage.content;

	useEffect(() => {
		fetchFirstPage(() => {
			const now = new Date();

			return notificationsService.getSentNotificationStatistics(
				props.courseId,
				timeZone,
				{
					sentAfter: addToDate(
						new Date(
							now.getFullYear(),
							now.getMonth(),
							now.getDate()
						).toISOString(),
						{days: daysBeforeToday}
					),
				},
				pageSize
			);
		});
	}, [fetchFirstPage, pageSize, props.courseId]);

	const notFetched = pageFetchStatus !== "succeeded";

	if (pageFetchStatus === "succeeded" && notifications.length === 0) {
		return (
			<Container
				sx={{
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					alignItems: "center",
					py: 7,
				}}
			>
				<Typography variant="h6">
					<Localized id="sent-notifications-widget-no-notifications">
						No notifications
					</Localized>
				</Typography>
				<Typography>
					<Localized id="sent-notifications-widget-no-notifications-descr">
						No notifications were sent in the past 30 days
					</Localized>
				</Typography>
			</Container>
		);
	}

	return (
		<Stack sx={{width: 1}}>
			<TableContainer>
				<Table sx={{tableLayout: "fixed", minWidth: 600}}>
					<TableHead>
						<TableRow>
							<TableCell def={columnDefs.notification}>
								{columnDefs.notification.name}
							</TableCell>
							<TableCell def={columnDefs.recipients}>
								{columnDefs.recipients.name}
							</TableCell>
							<TableCell def={columnDefs.date}>
								{columnDefs.date.name}
							</TableCell>
							<TableCell def={actionColumn} />
						</TableRow>
					</TableHead>

					<TableBody>
						{pageFetchStatus === "pending" && notifications.length === 0 && (
							<>
								<GroupRowPlaceholder />
								<GroupRowPlaceholder />
								<GroupRowPlaceholder />
							</>
						)}

						{pageFetchStatus === "failed" && (
							<LoadingErrorState
								description={
									<Localized id="sent-notifications-widget-error-fetch-notifications">
										Something has gone wrong, and we cannot load notifications
									</Localized>
								}
								columnNumber={columnNumber}
								onReload={retryFetching}
								slotProps={{container: {sx: {pb: 6}}}}
							/>
						)}

						{notifications.map((n) => (
							<StatisticsRow
								key={n.notificationName + n.sentDate}
								courseId={props.courseId}
								stats={n}
							/>
						))}
					</TableBody>
				</Table>
			</TableContainer>

			<ServerPaginationControls
				disabledControls={{
					first: notFetched || !notificationsPage.request.first,
					next: notFetched || !notificationsPage.request.next,
					previous: notFetched || !notificationsPage.request.previous,
				}}
				pageSize={pageSize}
				label={
					<Localized id="sent-notifications-widget-label-results-per-page">
						Results per page
					</Localized>
				}
				onPageSizeChange={setPageSize}
				onClick={fetchRelatedPage}
			/>
		</Stack>
	);
}

function StatisticsRow(props: {
	courseId: number;
	stats: SentNotificationStatistics;
}) {
	const {stats} = props;

	const [expanded, setExpanded] = useState(false);

	const formatDate = useDateFormat();
	const {l10n} = useLocalization();

	return (
		<>
			<TableRow hover>
				<TableCell component="th" scope="row" def={columnDefs.notification}>
					<Localized
						id="course-settings-notifications-notification-title"
						vars={{name: stats.notificationName}}
					/>
				</TableCell>
				<TableCell def={columnDefs.recipients}>
					{stats.numberOfRecipients}
				</TableCell>
				<TableCell def={columnDefs.date}>
					{formatDate(parseDate(stats.sentDate))}
				</TableCell>
				<TableCell def={actionColumn}>
					<IconButton
						aria-label={
							expanded
								? l10n.getString(
										"sent-notifications-widget-action-collapse-recipient-list"
								  )
								: l10n.getString(
										"sent-notifications-widget-action-expand-recipient-list"
								  )
						}
						onClick={() => setExpanded((prev) => !prev)}
					>
						{expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
					</IconButton>
				</TableCell>
			</TableRow>

			<TableRow>
				<TableCell
					colSpan={columnNumber}
					sx={{padding: 0, borderBottom: "unset"}}
				>
					<Collapse in={expanded} mountOnEnter>
						<SentNotificationRows courseId={props.courseId} stats={stats} />
					</Collapse>
				</TableCell>
			</TableRow>
		</>
	);
}

function SentNotificationRows(props: {
	courseId: number;
	stats: SentNotificationStatistics;
}) {
	const [notifications, setNotifications] = useState<
		CourseNotificationSearchResult[]
	>([]);

	const [recipients, setRecipients] = useState<Map<number, User>>(new Map());

	const [status, setStatus] = useState<FetchStatus>("none");

	const formatDateTime = useDateTimeFormat({dateStyle: "medium"});
	const {l10n} = useLocalization();

	async function fetchNotifications(
		courseId: number,
		name: string,
		date: string
	) {
		setStatus("pending");

		try {
			const res = await notificationsService.searchCourseNotifications(
				courseId,
				{
					name,
					sentAfter: date,
					sentBefore: addToDate(date, {days: 1}),
				}
			);

			setNotifications(res.content);

			if (res.related) {
				setRecipients(res.related.recipient);
			}

			setStatus("succeeded");
		} catch {
			setStatus("failed");
		}
	}

	useEffect(() => {
		fetchNotifications(
			props.courseId,
			props.stats.notificationName,
			props.stats.sentDate
		);
	}, [props.courseId, props.stats.sentDate, props.stats.notificationName]);

	const unknownRecipient: User = {
		id: 0,
		name: l10n.getString("sent-notifications-widget-unknown-reporter-name"),
	};

	return (
		<Table sx={{tableLayout: "fixed", width: 1}}>
			<TableBody>
				{status === "pending" && <NotificationRowPlaceholder />}

				{status === "failed" && (
					<LoadingErrorState
						description={
							<Localized id="sent-notifications-widget-error-fetch-notifications">
								Something has gone wrong, and we cannot load notifications
							</Localized>
						}
						columnNumber={columnNumber}
						onReload={() =>
							fetchNotifications(
								props.courseId,
								props.stats.notificationName,
								props.stats.sentDate
							)
						}
					/>
				)}

				{notifications.map((n, i) => {
					const recipient = recipients.get(n.recipientId) ?? unknownRecipient;

					return (
						<TableRow key={i} hover>
							<TableCell
								def={columnDefs.notification}
								component="th"
								scope="row"
								sx={{pl: 4}}
							>
								<StudentName
									student={{
										userName: recipient.name,
										firstName: recipient.firstName,
										lastName: recipient.lastName,
									}}
								/>
							</TableCell>
							<TableCell def={columnDefs.recipients} />
							<TableCell def={columnDefs.date}>
								{formatDateTime(parseDate(n.timestamp))}
							</TableCell>
							<TableCell def={actionColumn} />
						</TableRow>
					);
				})}
			</TableBody>
		</Table>
	);
}
function GroupRowPlaceholder() {
	return (
		<TableRow>
			<TableCell def={columnDefs.notification}>
				<Typography variant="inherit">
					<Skeleton width={`${randomN(20, 30)}ch`} />
				</Typography>
			</TableCell>
			<TableCell def={columnDefs.recipients}>
				<Typography variant="inherit">
					<Skeleton width="2ch" />
				</Typography>
			</TableCell>
			<TableCell def={columnDefs.date}>
				<Typography variant="inherit">
					<Skeleton width="10ch" />
				</Typography>
			</TableCell>
			<TableCell def={actionColumn} />
		</TableRow>
	);
}

function NotificationRowPlaceholder() {
	return (
		<TableRow>
			<TableCell def={columnDefs.notification} sx={{pl: 4}}>
				<StudentName loading student={{userName: ""}} />
			</TableCell>
			<TableCell def={columnDefs.recipients} />
			<TableCell def={columnDefs.date}>
				<Typography variant="inherit">
					<Skeleton width="15ch" />
				</Typography>
			</TableCell>
			<TableCell def={actionColumn} />
		</TableRow>
	);
}

export default SentNotificationsWidget;
